import {
  SOCIAL_SECURITY_CARD_CONFIG,
  type SocialSecurityCardContext,
  type SocialSecurityCardData,
  type SocialSecurityCardExtraction,
} from "@asap/shared/document/document-types/social-security-card";
import { SocialSecurityCardSchemas } from "@asap/shared/schemas/document/document-types/social-security-card";
import { formatSocialSecurityNumber } from "@asap/shared/utils/french-social-security-number";
import { formatFirstName, formatLastName } from "@asap/shared/utils/string";
import { DocumentTypeHandler } from "../handler";

/**
 * Social Security Card Handler (Carte vitale)
 */
export const SocialSecurityCardHandler = new DocumentTypeHandler<
  SocialSecurityCardExtraction,
  SocialSecurityCardData,
  SocialSecurityCardContext
>({
  // Identity
  documentType: SOCIAL_SECURITY_CARD_CONFIG.documentType,
  documentName: SOCIAL_SECURITY_CARD_CONFIG.documentName,

  // Constraints
  maxFiles: SOCIAL_SECURITY_CARD_CONFIG.maxFiles,
  acceptedMimeTypes: SOCIAL_SECURITY_CARD_CONFIG.acceptedMimeTypes,
  requiredFields: SOCIAL_SECURITY_CARD_CONFIG.requiredFields,

  // API endpoints
  endpoints: {
    extract: "documents/social-security-card/extract",
    warn: "documents/social-security-card/warn",
    verify: "documents/social-security-card/verify",
    store: "documents/social-security-card/store",
  },

  // Response schemas for runtime validation
  schemas: {
    extractResponse: SocialSecurityCardSchemas.extractResponse,
    warnResponse: SocialSecurityCardSchemas.warnResponse,
    validateResponse: SocialSecurityCardSchemas.validateResponse,
    storeResponse: SocialSecurityCardSchemas.storeResponse,
  },

  // Toast messages
  toastMessages: {
    success: "La carte vitale a bien été validée.",
  },

  // Form fields
  formFields: [
    {
      key: "social_security_number",
      label: SOCIAL_SECURITY_CARD_CONFIG.fieldLabels.social_security_number,
      description: "Affiché le temps de la saisie, il n'est pas enregistré.",
      type: "text",
      disabled: true,
      copyable: true,
      format: formatSocialSecurityNumber,
      // Hidden once stored, since nothing is persisted to display.
      visible: (formData) => Boolean(formData.social_security_number),
      colSpan: 2,
    },
  ],

  // Transform OCR extraction to form data
  transformExtraction: (extraction: SocialSecurityCardExtraction): Partial<SocialSecurityCardData> => {
    if (!extraction.isValidDocumentType) {
      throw new Error(
        "Le fichier ajouté ne semble pas correspondre à une carte vitale. Vérifie que ce soit bien le cas."
      );
    }

    const hasAnyExtractedField = extraction.first_name || extraction.last_name || extraction.social_security_number;

    if (!hasAnyExtractedField) {
      throw new Error("Le document n'a pas pu être lu. Merci de fournir une carte vitale lisible.");
    }

    const result: Partial<SocialSecurityCardData> = {};

    // first_name/last_name are extracted for the talent-name coherence warning only —
    // intentionally not exposed as form fields.
    if (extraction.first_name) result.first_name = formatFirstName(extraction.first_name);
    if (extraction.last_name) result.last_name = formatLastName(extraction.last_name);
    if (extraction.social_security_number) result.social_security_number = extraction.social_security_number;

    return result;
  },
});
